import { useMemo } from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import {
  Bar,
  CartesianGrid,
  ComposedChart,
  LabelList,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { BdcDashboardGranularity, BdcDashboardSeriesPoint } from "../../../../types/bdcDashboard.type";
import { SURFACE } from "../../../common/designTokens";
import { baht, pcs } from "../../format";
import { CHART_COLORS } from "../constants";
import { compactNumber, SeriesRow, toSeriesRows } from "../format";

const HEIGHT = 250;

function LegendKey({ kind, label }: Readonly<{ kind: "bar" | "dot"; label: string }>) {
  const color = kind === "bar" ? CHART_COLORS.ordered : CHART_COLORS.delivered;
  return (
    <Box component="span" sx={{ display: "inline-flex", alignItems: "center", gap: 0.75 }}>
      <Box
        component="span"
        sx={
          kind === "bar"
            ? { width: 12, height: 12, borderRadius: "3px", bgcolor: color }
            : { width: 9, height: 9, borderRadius: "50%", bgcolor: color, boxShadow: "0 0 0 2px #fff" }
        }
      />
      <span style={{ fontSize: 12, color: SURFACE.tx2 }}>{label}</span>
    </Box>
  );
}

export function SeriesLegend() {
  return (
    <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
      <LegendKey kind="bar" label="ยอดสั่ง" />
      <LegendKey kind="dot" label="ยอดส่ง" />
    </Box>
  );
}

/**
 * Pieces per period: ยอดสั่ง as bars, ยอดส่ง as dots on the same axis (one scale — both are
 * pieces). Only the latest bar carries a direct label; the tooltip gives every period's
 * ordered / delivered / ฿.
 */
export default function SeriesChart({
  series,
  granularity,
}: Readonly<{ series: BdcDashboardSeriesPoint[]; granularity: BdcDashboardGranularity }>) {
  const data = useMemo(() => toSeriesRows(series, granularity), [series, granularity]);
  const lastIndex = data.length - 1;

  if (data.length === 0) {
    return (
      <Box sx={{ height: HEIGHT, display: "flex", alignItems: "center", justifyContent: "center" }}>
        <Typography variant="body2" color="text.secondary">
          ไม่มีข้อมูลในช่วงที่เลือก
        </Typography>
      </Box>
    );
  }

  return (
    <Box role="img" aria-label="กราฟยอดสั่งและยอดส่งเป็นชิ้น">
      <ResponsiveContainer width="100%" height={HEIGHT}>
        <ComposedChart data={data} margin={{ top: 22, right: 8, left: 0, bottom: 0 }} barCategoryGap="30%">
          <CartesianGrid stroke={CHART_COLORS.grid} vertical={false} />
          <XAxis
            dataKey="tick"
            tick={{ fill: CHART_COLORS.ink, fontSize: 11 }}
            axisLine={{ stroke: CHART_COLORS.axis }}
            tickLine={false}
            interval={0}
          />
          <YAxis
            tick={{ fill: CHART_COLORS.ink, fontSize: 11 }}
            axisLine={false}
            tickLine={false}
            width={44}
            domain={[0, "auto"]}
            allowDecimals={false}
            tickFormatter={(value: number) => compactNumber(value)}
          />
          <Tooltip
            cursor={{ fill: "rgba(54,58,68,0.06)" }}
            content={({ active, payload }) => {
              if (!active || !payload?.length) return null;
              const row = payload[0].payload as SeriesRow;
              return (
                <Box
                  sx={{
                    bgcolor: "#fff",
                    border: `1px solid ${SURFACE.line}`,
                    borderRadius: 1,
                    px: 1.5,
                    py: 1,
                    boxShadow: "0 2px 10px rgba(0,0,0,0.08)",
                    minWidth: 170,
                  }}
                >
                  <Typography variant="caption" fontWeight={700} display="block">
                    {row.title}
                  </Typography>
                  {[
                    { key: "o", swatch: CHART_COLORS.ordered, label: "ยอดสั่ง", value: `${pcs(row.ordered_qty)} ชิ้น` },
                    { key: "d", swatch: CHART_COLORS.delivered, label: "ยอดส่ง", value: `${pcs(row.delivered_qty)} ชิ้น` },
                  ].map((item) => (
                    <Box key={item.key} sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Box sx={{ width: 8, height: 8, borderRadius: "2px", bgcolor: item.swatch }} />
                      <Typography variant="caption" color="text.secondary">
                        {item.label}
                      </Typography>
                      <Typography variant="caption" sx={{ ml: "auto", fontVariantNumeric: "tabular-nums" }}>
                        {item.value}
                      </Typography>
                    </Box>
                  ))}
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                    ฿{baht(row.ordered_amount)} ก่อน VAT
                  </Typography>
                </Box>
              );
            }}
          />
          <Bar
            dataKey="ordered_qty"
            name="ยอดสั่ง"
            fill={CHART_COLORS.ordered}
            radius={[4, 4, 0, 0]}
            maxBarSize={56}
            isAnimationActive={false}
          >
            <LabelList
              dataKey="ordered_qty"
              position="top"
              offset={8}
              content={(props) => {
                const { x, y, width, value, index } = props as {
                  x?: number | string;
                  y?: number | string;
                  width?: number | string;
                  value?: number | string;
                  index?: number;
                };
                if (index !== lastIndex || x === undefined || y === undefined) return null;
                return (
                  <text
                    x={Number(x) + Number(width ?? 0) / 2}
                    y={Number(y) - 8}
                    textAnchor="middle"
                    fontSize={12}
                    fontWeight={500}
                    fill={SURFACE.tx}
                  >
                    {pcs(Number(value))}
                  </text>
                );
              }}
            />
          </Bar>
          <Line
            dataKey="delivered_qty"
            name="ยอดส่ง"
            stroke="none"
            isAnimationActive={false}
            dot={{ r: 4.5, fill: CHART_COLORS.delivered, stroke: "#fff", strokeWidth: 2 }}
            activeDot={{ r: 6, fill: CHART_COLORS.delivered, stroke: "#fff", strokeWidth: 2 }}
          />
        </ComposedChart>
      </ResponsiveContainer>
    </Box>
  );
}
